const ITALY = 'IT';

// The backend uppercases an Italian Province, then accepts only a 2-letter sigla.
const ITALIAN_PROVINCE = /^[A-Za-z]{2}$/;
const ITALIAN_PROVINCE_MAX_LENGTH = 2;
const PROVINCE_MAX_LENGTH = 30;

const FORM_FIELDS = ['name', 'address', 'city', 'zip', 'countryCode', 'province', 'phoneNumber'];

const isBlank = value => value === null || value === undefined || String(value).trim() === '';

const orEmpty = value => (isBlank(value) ? '' : value);

const pick = (source, fields, valueOf = field => source[field]) => fields
  .reduce((picked, field) => ({ ...picked, [field]: valueOf(field) }), {});

/* ---------- Form ---------- */

export const addressRules = (countryCode) => {
  const isItalian = countryCode === ITALY;
  return {
    name: 'required',
    countryCode: 'required',
    province: isItalian ? { required: true, regex: ITALIAN_PROVINCE } : '',
  };
};

export const provinceMaxLength = countryCode => (
  countryCode === ITALY ? ITALIAN_PROVINCE_MAX_LENGTH : PROVINCE_MAX_LENGTH
);

// The label of an address in the picker: its non-blank name and street.
export const addressLabel = ({ name, address }) => [name, address]
  .filter(part => !isBlank(part))
  .join(' - ');

const SEARCHABLE_FIELDS = ['name', 'address', 'city'];

// Whether an address matches the picker search:
// a case-insensitive substring of any searchable field.
export const addressMatches = (address, query) => {
  if (isBlank(query)) return true;
  const needle = query.trim().toLowerCase();
  const contains = value => !isBlank(value) && String(value).toLowerCase().includes(needle);
  return SEARCHABLE_FIELDS.some(field => contains(address[field]));
};

/* ---------- Payloads ---------- */

// A new address sends only its filled fields: the backend rejects null but accepts a missing one.
export const newAddressPayload = (address) => {
  const filledFields = Object.keys(address).filter(field => !isBlank(address[field]));
  return pick(address, filledFields);
};

// An edited address sends only the changed fields, a cleared one as ''.
export const addressChanges = (stored, form) => {
  const isChanged = field => orEmpty(form[field]) !== orEmpty(stored[field]);
  const changedFields = FORM_FIELDS.filter(isChanged);
  return pick(form, changedFields, field => orEmpty(form[field]));
};

/* ---------- Errors ---------- */

const ERRORS = 'samples.create.address.errors';

// LoopBack's request-schema check; its messages are English only and are never shown.
const SCHEMA_ERROR = 'VALIDATION_FAILED';

const detailField = ({ path, info }) => (info && info.missingProperty) || (path || '').replace(/^\//, '');

const genericFieldMessage = ({ code, info }, field, i18n) => {
  const label = i18n.t(`samples.create.address.${field}`);
  if (code === 'required') return i18n.t(`${ERRORS}.required`, { field: label });
  if (code === 'maxLength' && info) {
    return i18n.t(`${ERRORS}.maxLength`, { field: label, limit: info.limit });
  }
  return i18n.t(`${ERRORS}.invalidField`, { field: label });
};

// A business-rule error may have its own message (e.g. `province.required`),
// a schema error never does.
const fieldMessage = (detail, field, isSchemaError, i18n) => {
  const specificKey = `${ERRORS}.${field}.${detail.code}`;
  if (!isSchemaError && i18n.te(specificKey)) return i18n.t(specificKey);
  return genericFieldMessage(detail, field, i18n);
};

const groupByField = (details, isSchemaError, i18n) => details.reduce((fields, detail) => {
  const field = detailField(detail);
  const message = fieldMessage(detail, field, isSchemaError, i18n);
  return { ...fields, [field]: [...(fields[field] || []), message] };
}, {});

const rejectionErrors = (backendError, i18n) => {
  const isSchemaError = backendError.code === SCHEMA_ERROR;
  const details = backendError.details || [];
  const formDetails = details.filter(detail => FORM_FIELDS.includes(detailField(detail)));

  const fields = groupByField(formDetails, isSchemaError, i18n);
  const messages = [].concat(...Object.values(fields));

  const hasProblemsOutsideForm = formDetails.length < details.length;
  if (hasProblemsOutsideForm || !messages.length) messages.push(i18n.t(`${ERRORS}.invalid`));

  return { fields, messages };
};

const failureErrors = message => ({ fields: {}, messages: [message] });

/**
 * Turns a failed address save into
 * - `fields`: { field: [message] } for ValidationObserver.setErrors;
 * - `messages`: the lines of the error notification, one per problem.
 * Every message is the client's own translation: backend text never reaches the user.
 */
export const addressErrors = (error, i18n) => {
  const { response } = error;
  if (!response) return failureErrors(i18n.t('global.connectionError'));

  switch (response.status) {
    case 422: return rejectionErrors((response.data && response.data.error) || {}, i18n);
    case 403: return failureErrors(i18n.t(`${ERRORS}.forbidden`));
    default: return failureErrors(i18n.t(`${ERRORS}.saveFailed`));
  }
};
